import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { searchCircleData } from "../../../states/actions/fetchCirclePosts";

import LoadingScreen from "../../common/LoadingScreen";
import LoaderSpinner from "../../common/LoaderSpinner";
import CirclePost from "../../common/CirclePost";
import paths from "../../../constants/paths";
import Text from "../../common/Text";
import Breadcrumb from "../../common/BreadCrumb";

const Circle = () => {
  const breadcrumbItems = [
    { name: "Dashboard", link: "/dashboard" },
    { name: "Learning Hub", link: "/learning-hub" },
  ];
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const firstLoad = useRef(true);

  const [keyword, setKeyword] = useState(searchParams.get("keyword") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [sortBy, setSortBy] = useState(searchParams.get("sort_by") || "desc");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [showCategories, setShowCategories] = useState(false);

  const data = useSelector((state) => state.fetchCirclePosts.content);

  const isLoading = useSelector((state) => state.fetchCirclePosts.isLoading);
  const isSearching = useSelector(
    (state) => state.fetchCirclePosts.isSearching
  );
  const isSiteSettingsLoading = useSelector(
    (state) => state.fetchSiteSettings.isLoading
  );
  // const activePlan = useSelector((state) => state.fetchSiteSettings.activePlan);
  const {
    circles = [],
    categories = [],
    page_title,
    total_post = 0,
    limit = 5,
    content,
  } = data;

  useDocumentTitle(page_title || "Circle");

  const toggleCategories = () => setShowCategories((prev) => !prev);

  const handleSearch = (syncUrl = true) => {
    if (syncUrl) {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (keyword) params.set("keyword", keyword);
      if (sortBy !== "desc") params.set("sort_by", sortBy);
      if (page > 1) params.set("page", page);
      setSearchParams(params);
    }

    dispatch(
      searchCircleData({
        category,
        keyword,
        sort_by: sortBy,
        token: localStorage.getItem("authToken"),
        pagenumber: page,
      })
    );
  };

  const handleClearFilters = () => {
    setKeyword("");
    setCategory("");
    setSortBy("desc");
    setPage(1);
    setSearchParams({});
    dispatch(
      searchCircleData({
        category: "",
        keyword: "",
        sort_by: "desc",
        token: localStorage.getItem("authToken"),
        pagenumber: 1,
      })
    );
  };

  useEffect(() => {
    handleSearch(false);
    firstLoad.current = false;
  }, []);

  useEffect(() => {
    if (!firstLoad.current) {
      handleSearch(true);
    }
  }, [category, sortBy, page]);

  const totalPages = Math.ceil(total_post / limit);

  // useEffect(() => {
  //   if (!isSiteSettingsLoading) {
  //     if (!activePlan || [1, 4, 5, 7].includes(Number(activePlan?.plan_id))) {
  //       window.location.href = `${paths.SITE_BASE_URL}/subscription`;
  //     }
  //   }
  // }, [isSiteSettingsLoading, activePlan]);

  return (
    <>
      {isLoading || isSiteSettingsLoading ? (
        <LoadingScreen />
      ) : (
        <section className="forum_banners">
          <Breadcrumb items={breadcrumbItems} />
          <div className="contain">
            <div className="content">
              <h1 className="heading">{content.banner_heading}</h1>
              <p className="add_text_emoji">
                <Text string={content.detail} parse={true} />
              </p>
            </div>

            <div className="flex posts">
              {/* Left Sidebar */}
              <div className="colL">
                <div className="inner">
                  <div className="filter_header">
                    <div className="cat_toggle_row" onClick={toggleCategories}>
                      <div className="flx">
                        <h4>Categories</h4>
                        <img
                          src={paths.SITE_BASE_URL + "/images/chevron.svg"}
                          alt="Toggle"
                          className={`chevron_icon ${
                            showCategories ? "rotate" : ""
                          }`}
                        />
                      </div>
                      <button type="button" onClick={handleClearFilters}>
                        Clear All
                      </button>
                    </div>
                  </div>
                  <div
                    className={`mobile_drop ${showCategories ? "show" : ""}`}
                  >
                    <ul>
                      {categories?.map((cat) => (
                        <li
                          key={cat.id}
                          className={`drop_cat ${
                            category === cat.slug ? "active" : ""
                          }`}
                          onClick={() => {
                            setCategory(cat.slug);
                            setPage(1);
                          }}
                        >
                          <div className="lbl_btn">
                            <span>{cat.title}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right Content */}
              <div className="colR">
                <div className="filter_top">
                  <div className="search_blk">
                    <input
                      type="text"
                      className="input"
                      placeholder="Search"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                    />
                    <button
                      onClick={() => {
                        setPage(1);
                        handleSearch(true);
                      }}
                    >
                      <img
                        src={paths.SITE_BASE_URL + "/images/search.svg"}
                        alt="search"
                        width="100"
                        height="100"
                      />
                    </button>
                  </div>

                  <div className="vertical_line">
                    <span></span>
                  </div>

                  <div className="sort_blk">
                    <span>Sort by :</span>
                    <select
                      className="input"
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="desc">Most Recent</option>
                      <option value="asc">Least Recent</option>
                    </select>
                  </div>
                </div>

                <div className="forum_posts_outer">
                  {isSearching ? (
                    <LoaderSpinner />
                  ) : circles.length > 0 ? (
                    <>
                      {circles.map((circle, index) => (
                        <CirclePost
                          key={circle.id}
                          circle={circle}
                          index={index}
                        />
                      ))}

                      {total_post > limit && (
                        <div className="d-flex justify-content-center gap-3 my-5 flex-wrap">
                          <button
                            className="btn btn-lg btn-outline-primary px-4"
                            disabled={page === 1}
                            onClick={() => setPage((prev) => prev - 1)}
                          >
                            Previous
                          </button>
                          {[...Array(totalPages)].map((_, i) => (
                            <button
                              key={i + 1}
                              className={`btn btn-lg px-4 ${
                                page === i + 1
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              onClick={() => setPage(i + 1)}
                            >
                              {i + 1}
                            </button>
                          ))}
                          <button
                            className="btn btn-lg btn-outline-primary px-4"
                            disabled={page >= totalPages}
                            onClick={() => setPage((prev) => prev + 1)}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="alert alert-danger text-center fw-medium my-3">
                      No Post Available!
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Circle;
